/**
 * WHAT THE STUDY TESTS, AND WHICH RULES THAT BRINGS (backlog 21 zz, extends
 * s; 2026-10-20). THE ONE DECISION POINT for every reader that frames a
 * document or a form by the product type: the regulatory pack, the SAERP and
 * DMP facts, the AE action and relationship fields, SUSAR fields, and the SDTM
 * action variable.
 *
 * Found on the Penn digital-health study (an app and a commercial wristband,
 * "IND/IDE Sponsor none"): the product type was TBD, and every rule fell back
 * to the DRUG rules. The DMP cited 21 CFR 312.32 IND safety reports, the SAERP
 * a Pre-DSUR reconciliation and a CAPA for a late "expedited IND safety
 * report to FDA", the SAE form asked about SUSARs, the action field on the app
 * offered "Dose reduced" and "Drug withdrawn", and SDTM derived AEACN = "DRUG
 * WITHDRAWN".
 *
 *   medicinal_product  drug rules (unchanged; every drug study reads as before)
 *   combination        drug rules AND device rules
 *   device             device rules; no IND safety report, SUSAR, DSUR or dose actions
 *   other              no investigational drug or device: none of those, and
 *                      serious events go to the ethics body as the protocol says
 *   TBD (null)         nothing is assumed; where a drug-only or device-only rule
 *                      would apply, the document says it is for the sponsor to
 *                      confirm. A failure to read the product type never reads
 *                      as a decision.
 *
 * Pure.
 */
import type { Fact, JurisdictionPack, ProductType, StudyProfile } from "./types";
import type { OperationsFacts } from "./operationsFromProtocol";

export const PRODUCT_TYPES: readonly ProductType[] = ["medicinal_product", "device", "combination", "other"];

export const PRODUCT_TYPE_NEEDED = "Product type (medicinal product, device, combination or other)";

type Rec = Record<string, unknown>;
const obj = (v: unknown): Rec => (v && typeof v === "object" && !Array.isArray(v) ? (v as Rec) : {});
const oneOf = (v: unknown): ProductType | null => {
  const s = typeof v === "string" ? v.trim().toLowerCase() : "";
  return (PRODUCT_TYPES as readonly string[]).includes(s) ? (s as ProductType) : null;
};

/**
 * The study's product type: Study Settings first, then what the protocol
 * states (analysis.operationsFacts, read deterministically from its text by
 * operationsFromProtocol.ts). The study profile and every reader that has no
 * profile (the CRF authority) call this, so they cannot disagree.
 */
export function resolveProductType(studySetup: unknown, analysis: unknown): Fact<ProductType> {
  const user = oneOf(obj(studySetup).productType);
  if (user) return { value: user, source: "user" };
  const ops = obj(obj(analysis).operationsFacts) as Partial<OperationsFacts>;
  const fromProtocol = oneOf(ops.productType?.value);
  if (fromProtocol) {
    return { value: fromProtocol, source: "protocol", ...(ops.productType?.evidence ? { evidence: ops.productType.evidence } : {}) };
  }
  return { value: null, source: null, needed: PRODUCT_TYPE_NEEDED };
}

export interface ProductFraming {
  type: ProductType | null;
  /** What participants receive, as a form or a document names it. */
  noun: "study drug" | "study device" | "study product" | "study intervention";
  /** IND safety reports (21 CFR 312.32), SUSARs, DSURs, dose actions (AEACN). null: TBD. */
  drugRules: boolean | null;
  /** Adverse device effects, device deficiencies, device reporting (21 CFR 812, EU MDR). null: TBD. */
  deviceRules: boolean | null;
  /** True only for "other": no investigational drug and no investigational device. */
  noInvestigationalProduct: boolean;
  /** The protocol's sentence, when the type came from the protocol. */
  evidence?: string;
}

const NOUN: Record<ProductType, ProductFraming["noun"]> = {
  medicinal_product: "study drug",
  device: "study device",
  combination: "study product",
  other: "study intervention",
};

export function productFraming(pt: ProductType | null | undefined | Fact<ProductType>): ProductFraming {
  const f = pt && typeof pt === "object" ? (pt as Fact<ProductType>) : null;
  const type = f ? f.value : ((pt as ProductType | null | undefined) ?? null);
  const evidence = f?.evidence;
  if (!type) return { type: null, noun: "study intervention", drugRules: null, deviceRules: null, noInvestigationalProduct: false };
  return {
    type,
    noun: NOUN[type],
    drugRules: type === "medicinal_product" || type === "combination",
    deviceRules: type === "device" || type === "combination",
    noInvestigationalProduct: type === "other",
    ...(evidence ? { evidence } : {}),
  };
}

/** The framing of a built profile. A profile built before 2026-09-30 has no product type: TBD. */
export function profileProductFraming(profile: Pick<StudyProfile, "design"> | null | undefined): ProductFraming {
  return productFraming(profile?.design?.productType ?? null);
}

// ── What each jurisdiction pack says, for this product ─────────────────────

/** "Therapeutic Goods Administration (TGA)" -> "TGA"; no trailing acronym: the text unchanged. */
function shortName(full: string): string {
  const m = /\(([A-Za-z]{2,8})\)\s*$/.exec(full.trim());
  return m ? m[1] : full.trim();
}

/**
 * The safety-reporting rule a pack contributes. `framing` null means the
 * caller knows only the countries (no product type), and gets the pack's own
 * rule as before. A drug or combination study: the pack's rule, unchanged.
 */
export function expeditedSafetyReportingFor(pack: JurisdictionPack, framing: ProductFraming | null): string {
  const reg = shortName(pack.regulator);
  const eth = shortName(pack.ethicsBody);
  const drugLine = pack.expeditedSafetyReporting ?? `${pack.name}: expedited reporting to ${reg} and ${eth} per local regulation (TBD: confirm timelines)`;
  if (!framing || framing.drugRules === true) return drugLine;
  if (framing.noInvestigationalProduct) {
    return `${pack.name}: no investigational drug or device, so no expedited safety reporting to ${reg} (no IND safety reports, SUSARs or DSUR); serious adverse events and unanticipated problems are reported to the ${eth} as the protocol and the ${eth}'s policy require`;
  }
  if (framing.deviceRules === true) {
    return pack.deviceSafetyReporting
      ?? `${pack.name}: device safety reporting to ${reg} and the ${eth} under the local medical-device rules (TBD: confirm the rule and its timelines)`;
  }
  return `${drugLine} [SPONSOR REVIEW: product type not recorded; this rule applies to medicinal-product trials only]`;
}

/** The pack's trial-authorisation regime, or "not applicable" when there is no investigational product. */
export function trialAuthorisationFor(pack: JurisdictionPack, framing: ProductFraming | null): string | undefined {
  if (framing?.noInvestigationalProduct) return NOT_APPLICABLE_AUTHORISATION;
  return pack.trialAuthorisation;
}

export const NOT_APPLICABLE_AUTHORISATION = "Not applicable: no investigational drug or device";

/** The pack's GCP basis. With no investigational product the drug and device trial rules do not apply. */
export function gcpBasisFor(pack: JurisdictionPack, framing: ProductFraming | null): string {
  if (!framing?.noInvestigationalProduct) return pack.gcpBasis;
  return `ICH E6(R3) principles and the ethics review above; the drug and device trial rules (${pack.gcpBasis}) do not apply, because there is no investigational drug or device`;
}

/** Where a quoted protocol sentence goes in a line: ` (protocol: "...")`, or nothing. */
function quoted(evidence: string | undefined): string {
  if (!evidence) return "";
  const e = evidence.replace(/\s+/g, " ").trim();
  return ` (protocol: "${e.length > 160 ? `${e.slice(0, 157)}...` : e}")`;
}

/**
 * The lines every generator reads (formatStudyProfile's "Investigational
 * product rules" section). A medicinal-product study gets NONE, so every drug
 * study's prompt is exactly what it was.
 */
export function productRuleLines(framing: ProductFraming): string[] {
  if (framing.type === "medicinal_product") return [];
  if (framing.type === "other") {
    return [
      `No investigational drug and no investigational device${quoted(framing.evidence)}. Call what participants receive "the study intervention".`,
      "Do NOT apply drug or device regulatory rules: no IND or IDE, no 21 CFR 312 or 812, no IND safety reports, no SUSARs, no DSUR or ICH E2F, no expedited reports to a regulator, no investigational-product accountability.",
      "Serious adverse events and unanticipated problems are reported to the ethics body (IRB / ethics committee) and the sponsor as the protocol states; state no regulator clock the protocol does not state.",
      "Adverse event fields ask about the study intervention or procedure: no dose changes, no \"drug withdrawn\", no relationship to a study drug.",
    ];
  }
  if (framing.type === "device") {
    return [
      `Medical device study${quoted(framing.evidence)}. Call the product "the study device".`,
      "Use device safety terms (adverse device effect, serious adverse device effect, device deficiency). Do NOT apply drug rules: no IND safety reports under 21 CFR 312, no SUSARs, no DSUR, no dose changes or \"drug withdrawn\".",
      "Device safety reporting follows the device rules of the study's countries listed under Jurisdiction; where none is listed, write TBD.",
    ];
  }
  if (framing.type === "combination") {
    return [
      `Combination product study${quoted(framing.evidence)}: drug rules apply to the drug constituent and device rules (adverse device effects, device deficiencies) to the device constituent.`,
    ];
  }
  return [
    "Product type TBD: it is not yet known whether the study tests a drug, a device or neither.",
    "Do not state drug-only obligations (IND, 21 CFR 312.32 IND safety reports, SUSARs, DSUR) or device-only obligations as applying. Where one would apply, write it with [SPONSOR REVIEW: product type not recorded; this applies only to a medicinal-product (or device) trial].",
  ];
}
